import { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'

import { ColorPickerController, DEFAULT_PRODUCT_COLOR } from '../../../components/forms/fields/ColorPickerController'
import { SelectController } from '../../../components/forms/fields/SelectController'
import { TextFieldController } from '../../../components/forms/fields/TextFieldController'
import { toast } from '../../../components/LsToast'
import { createProduct, updateProduct } from '../../../service/products.service'
import { LessonType, ProductDto } from '../../../service/types'
import { ProductFormValues, useSchema } from '../schema'

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  product?: ProductDto | null
}

export const ProductDialog: FC<Props> = ({ open, onClose, onSaved, product }) => {
  const { t } = useTranslation()
  const schema = useSchema()

  const { handleSubmit, control, reset, watch, setValue } = useForm<ProductFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      lessonType: 'private',
      durationMinutes: 60,
      price: 0,
      pricePerAdditionalPerson: 0 as number | null,
      vatRate: 21,
      color: DEFAULT_PRODUCT_COLOR,
      isActive: true,
    },
  })

  useEffect(() => {
    if (product) {
      reset({
        name: product.name,
        lessonType: product.lessonType,
        durationMinutes: product.durationMinutes,
        price: product.price,
        pricePerAdditionalPerson: product.pricePerAdditionalPerson ?? 0,
        vatRate: product.vatRate,
        color: (product.color as ProductFormValues['color']) ?? DEFAULT_PRODUCT_COLOR,
        isActive: product.isActive,
      })
    } else {
      reset({
        name: '',
        lessonType: 'private',
        durationMinutes: 60,
        price: 0,
        pricePerAdditionalPerson: 0,
        vatRate: 21,
        color: DEFAULT_PRODUCT_COLOR,
        isActive: true,
      })
    }
  }, [product, open, reset])

  const isActive = watch('isActive')
  const lessonType = watch('lessonType')
  const isPrivate = lessonType === 'private'

  useEffect(() => {
    if (!isPrivate) {
      setValue('pricePerAdditionalPerson', null)
    }
  }, [isPrivate, setValue])

  const onSubmit = async (data: ProductFormValues) => {
    try {
      const payload = {
        name: data.name,
        lessonType: data.lessonType as LessonType,
        durationMinutes: data.durationMinutes,
        price: data.price,
        pricePerAdditionalPerson: data.lessonType === 'private' ? data.pricePerAdditionalPerson : null,
        vatRate: data.vatRate,
        color: data.color,
        isActive: data.isActive,
      }

      if (product) {
        await updateProduct(product.id, payload)
      } else {
        await createProduct(payload)
      }
      toast.success(t('general.notification.savedSuccessfully'))
      onSaved()
      onClose()
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick') onClose()
      }}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {product ? t('products.editProduct') : t('products.addProduct')}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption">{t('products.form.name')}</Typography>
              <TextFieldController name="name" control={control} />
            </Box>
            <Box display="flex" gap={2}>
              <Box flex={1}>
                <Typography variant="caption">{t('products.form.lessonType')}</Typography>
                <SelectController
                  name="lessonType"
                  control={control}
                  options={[
                    <MenuItem key="private" value="private">
                      {t('lessonType.private')}
                    </MenuItem>,
                    <MenuItem key="group" value="group">
                      {t('lessonType.group')}
                    </MenuItem>,
                  ]}
                />
              </Box>
              <Box flex={1}>
                <Typography variant="caption">{t('products.form.durationMinutes')}</Typography>
                <TextFieldController name="durationMinutes" control={control} type="number" />
              </Box>
            </Box>
            <Box display="flex" gap={2}>
              <Box flex={1}>
                <Typography variant="caption">{t('products.form.price')}</Typography>
                <TextFieldController name="price" control={control} type="number" />
              </Box>
              {isPrivate && (
                <Box flex={1}>
                  <Typography variant="caption">{t('products.form.pricePerAdditionalPerson')}</Typography>
                  <TextFieldController name="pricePerAdditionalPerson" control={control} type="number" />
                </Box>
              )}
            </Box>
            <ColorPickerController name="color" control={control} label={t('products.form.color')} />
            <Box display="flex" gap={2}>
              <Box flex={1}>
                <Typography variant="caption">{t('products.form.vatRate')}</Typography>
                <TextFieldController name="vatRate" control={control} type="number" />
              </Box>
              <Box flex={1} display="flex" alignItems="flex-end">
                <FormControlLabel
                  control={<Switch checked={isActive} onChange={(e) => setValue('isActive', e.target.checked)} />}
                  label={t('products.form.isActive')}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('general.button.cancel')}</Button>
          <Button type="submit" variant="contained">
            {t('general.button.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
